import { useState, useEffect } from "react";
import { API } from "@/App";
import { Shield, Users, Activity, Plus, X, Database } from "lucide-react";
import { ADMIN_EMAILS } from "@/config/admin";

export default function AdminDashboard() {
  const [sessions, setSessions] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [dbHealth, setDbHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantError, setGrantError] = useState("");
  const currentUser = localStorage.getItem("wokelo_username") || "";

  useEffect(() => {
    Promise.all([
      fetch(`${API}/admin/sessions`).then(r => r.json()).catch(() => ({ sessions: [] })),
      fetch(`${API}/admin/db-health`).then(r => r.json()).catch(() => ({ status: "unknown" })),
      fetch(`${API}/admin/admins`).then(r => r.json()).catch(() => ({ admins: [] })),
    ]).then(([sessData, healthData, adminData]) => {
      setSessions(sessData.sessions || []);
      setDbHealth(healthData);
      setAdminUsers(adminData.admins || []);
      setLoading(false);
    });
  }, []);

  const handleGrant = async () => {
    if (!grantEmail.trim()) return;
    setGrantError("");
    try {
      const res = await fetch(`${API}/admin/grant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: grantEmail.trim(), granted_by: currentUser }),
      });
      if (!res.ok) throw new Error("Failed to grant");
      setAdminUsers(prev => [...prev, { email: grantEmail.trim(), granted_by: currentUser, granted_at: new Date().toISOString() }]);
      setGrantEmail("");
    } catch {
      setGrantError("Failed to grant admin access");
    }
  };

  const handleRevoke = async (email) => {
    try {
      await fetch(`${API}/admin/revoke`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setAdminUsers(prev => prev.filter(a => a.email !== email));
    } catch {}
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="p-8" style={{ background: "#FFFFFF", minHeight: "100%" }}>
        <div className="animate-pulse space-y-4 max-w-5xl mx-auto">
          <div className="h-8 w-48 bg-[#E5E7EB] rounded" />
          <div className="h-64 bg-[#F3F4F6] rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#FFFFFF", minHeight: "100%", padding: "28px 32px" }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Shield size={22} className="text-[#1A1A1A]" />
          <h1 className="text-[26px] font-bold text-[#0A0A0A]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Admin Dashboard
          </h1>
        </div>

        {/* DB Health */}
        {dbHealth && (
          <div
            className="flex items-center gap-3 p-4 rounded-lg mb-6"
            style={{
              border: "1px solid #E5E7EB",
              background: dbHealth.status === "healthy" ? "#F0FDF4" : "#FEF2F2",
            }}
            data-testid="db-health"
          >
            <Database size={16} className={dbHealth.status === "healthy" ? "text-[#16A34A]" : "text-[#DC2626]"} />
            <span className="text-[13px] font-medium" style={{ color: dbHealth.status === "healthy" ? "#16A34A" : "#DC2626" }}>
              Database: {dbHealth.status === "healthy" ? "Connected" : "Disconnected"}
            </span>
          </div>
        )}

        {/* User Sessions */}
        <div className="mb-8" data-testid="sessions-section">
          <div className="flex items-center gap-2 mb-4">
            <Activity size={16} className="text-[#6B7280]" />
            <h2 className="text-[16px] font-semibold text-[#1A1A1A]">User Sessions</h2>
            <span className="text-[12px] text-[#9AA0A6] ml-2">{sessions.length} users tracked</span>
          </div>
          <div className="bg-white rounded-lg overflow-hidden" style={{ border: "1px solid #E5E7EB" }}>
            <table className="w-full" data-testid="sessions-table">
              <thead>
                <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                  {["Email", "Last Login", "Total Sessions", "Admin"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[13px] text-[#9AA0A6]">
                      No sessions recorded yet
                    </td>
                  </tr>
                ) : (
                  sessions.map((s, i) => {
                    const isHardcoded = ADMIN_EMAILS.some(e => e.toLowerCase() === (s.email || "").toLowerCase());
                    const isDynamic = adminUsers.some(a => a.email?.toLowerCase() === (s.email || "").toLowerCase());
                    return (
                      <tr
                        key={s.email || i}
                        style={{ borderBottom: "1px solid #F0F2F4", background: i % 2 === 0 ? "#FFFFFF" : "#FAFAFA" }}
                      >
                        <td className="px-4 py-3 text-[13px] text-[#1A1A1A] font-medium">{s.email}</td>
                        <td className="px-4 py-3 text-[12px] text-[#6B7280]">{formatDate(s.last_login)}</td>
                        <td className="px-4 py-3 text-[12px] text-[#6B7280]">{s.total_sessions || 0}</td>
                        <td className="px-4 py-3">
                          {(isHardcoded || isDynamic) ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-[#DBEAFE] text-[#2563EB]">
                              Admin
                            </span>
                          ) : (
                            <span className="text-[11px] text-[#D1D5DB]">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Admin Management */}
        <div data-testid="admin-management">
          <div className="flex items-center gap-2 mb-4">
            <Users size={16} className="text-[#6B7280]" />
            <h2 className="text-[16px] font-semibold text-[#1A1A1A]">Admin Access Management</h2>
          </div>

          {/* Hardcoded admins */}
          <div className="mb-4">
            <p className="text-[11px] font-semibold text-[#9AA0A6] uppercase tracking-wider mb-2">Permanent Admins</p>
            <div className="flex flex-wrap gap-2">
              {ADMIN_EMAILS.map(email => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium"
                  style={{ background: "#F3F4F6", color: "#374151" }}
                >
                  <Shield size={12} />
                  {email}
                </span>
              ))}
            </div>
          </div>

          {/* Dynamic admins */}
          <div className="mb-4">
            <p className="text-[11px] font-semibold text-[#9AA0A6] uppercase tracking-wider mb-2">Granted Admins</p>
            {adminUsers.length === 0 ? (
              <p className="text-[12px] text-[#9AA0A6] italic">No additional admins granted</p>
            ) : (
              <div className="flex flex-col gap-2">
                {adminUsers.map(a => (
                  <div
                    key={a.email}
                    className="flex items-center justify-between px-3 py-2 rounded-lg"
                    style={{ border: "1px solid #E5E7EB" }}
                  >
                    <div>
                      <span className="text-[13px] font-medium text-[#1A1A1A]">{a.email}</span>
                      <span className="text-[11px] text-[#9AA0A6] ml-3">
                        Granted by {a.granted_by || "system"} on {formatDate(a.granted_at)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRevoke(a.email)}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-[#DC2626] hover:bg-[#FEE2E2] transition-colors"
                      data-testid={`revoke-${a.email}`}
                    >
                      <X size={12} /> Revoke
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Grant form */}
          <div className="flex items-center gap-2 mt-4">
            <input
              type="email"
              value={grantEmail}
              onChange={e => setGrantEmail(e.target.value)}
              placeholder="Enter email to grant admin..."
              className="flex-1 h-9 px-3 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              style={{ border: "1px solid #D1D5DB" }}
              data-testid="grant-email-input"
              onKeyDown={e => e.key === "Enter" && handleGrant()}
            />
            <button
              onClick={handleGrant}
              disabled={!grantEmail.trim()}
              className="flex items-center gap-1.5 h-9 px-4 rounded-md text-[13px] font-medium text-white disabled:opacity-50 transition-colors"
              style={{ background: "#1A1A1A" }}
              data-testid="grant-admin-btn"
            >
              <Plus size={14} /> Grant Access
            </button>
          </div>
          {grantError && <p className="text-[12px] text-[#DC2626] mt-2">{grantError}</p>}
        </div>
      </div>
    </div>
  );
}
